import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from './DashboardLayout';
import StepSelectToolType from './billToFlowSteps/StepSelectToolType';
import StepConfigureTool from './billToFlowSteps/StepConfigureTool';
import StepSetParameters from './billToFlowSteps/StepSetParameters';
import StepReviewConfirm from './billToFlowSteps/StepReviewConfirm';
import StepToolCreated from './billToFlowSteps/StepToolCreated';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import {
  useDeleteMyToolMutation,
  useGetMyToolByIdQuery,
  useLazyGetMyToolByIdQuery,
  useLazyGetMyToolDraftQuery,
  useUpdateMyToolMutation,
} from '../../redux/myToolsApi';
import { useGetProductTypesQuery } from '../../redux/productTypeApi';
import { useGetDailySalesQuery } from '../../redux/dailySalesApi';
import { listFromApiResponse } from '../../utils/billToItemCatalog';
import {
  listDailySalesFromApi,
  mapApiDailySaleRow,
} from '../../utils/billToDailySales';
import {
  buildCreateMyToolPayload,
  clearDraftWizardStep,
  hydrateWizardFromMyTool,
  isIncompleteMyToolDraft,
  loadDraftWizardStep,
  mergeMyToolProjectIds,
  myToolIdFromResponse,
  myToolProjectIdsFromResponse,
  myToolRowFromCreateResponse,
  saveDraftWizardStep,
  wizardStateFromMyToolResponse,
} from '../../utils/billToFlowTools';
import { selectedSkuPlanningComplete } from '../../utils/billToItemCatalog';

const STEPS = [
  { label: 'Select Operating Platform' },
  { label: 'Configure Tool' },
  { label: 'Set Parameters' },
  { label: 'Review & Confirm' },
  { label: 'Tool Created' },
];

const INITIAL_DATA = {
  id: '',
  toolType: '',
  productTypeId: '',
  productTypeName: '',
  categories: [],
  subCategories: [],
  selectedItemKeys: null,
  planningByCategory: {},
  planningBySubcategory: {},
  planningBySku: {},
  categoryLabels: {},
  subCategoryLabels: {},
  productCatalog: [],
  dailySalesRows: [],
  categoriesDefaulted: false,
  toolName: '',
  toolCode: '',
  toolDescription: '',
  toolDate: '',
  outletId: '',
  outletName: '',
  closingStockDraft: null,
  toolStatus: '',
  myToolProjectIds: {},
  parameters: {
    salesHistoryWeeks: '',
    projectedSales: '',
    projectedGc: '',
    week1Gc: '',
    week2Gc: '',
    week3Gc: '',
    week1Sales: '',
    week2Sales: '',
    week3Sales: '',
    week1Label: '',
    week2Label: '',
    week3Label: '',
    includeClosingStock: true,
    includeIssuedStock: true,
    includeProductionStock: false,
    includeDamagedStock: false,
    includeReturnsStock: false,
  },
};

function canProceed(step, data) {
  if (step === 0) return Boolean(data.toolType);
  if (step === 1) {
    const selectedKeys = data.selectedItemKeys;
    const hasProducts = !Array.isArray(selectedKeys) || selectedKeys.length > 0;
    const p = data.parameters || {};
    return (
      Boolean(String(data.outletId || data.outletName || '').trim()) &&
      String(data.toolDate || '').trim() !== '' &&
      String(p.salesHistoryWeeks ?? '').trim() !== '' &&
      data.categories.length > 0 &&
      hasProducts &&
      String(p.week1Gc ?? '').trim() !== '' &&
      String(p.week2Gc ?? '').trim() !== '' &&
      String(p.week3Gc ?? '').trim() !== '' &&
      String(p.week1Sales ?? '').trim() !== '' &&
      String(p.week2Sales ?? '').trim() !== '' &&
      String(p.week3Sales ?? '').trim() !== '' &&
      String(data.id || '').trim() !== ''
    );
  }
  if (step === 2) {
    const p = data.parameters || {};
    return (
      selectedSkuPlanningComplete(data) &&
      String(p.projectedSales ?? '').trim() !== '' &&
      String(p.projectedGc ?? '').trim() !== '' &&
      String(data.toolName || '').trim() !== ''
    );
  }
  return true;
}

function Stepper({ currentStep }) {
  return (
    <div className="flex items-center justify-between gap-1">
      {STEPS.map((step, idx) => {
        const isCompleted = idx < currentStep;
        const isCurrent = idx === currentStep;
        const isUpcoming = idx > currentStep;

        return (
          <React.Fragment key={step.label}>
            {idx > 0 && (
              <div
                className={`hidden h-0.5 flex-1 sm:block ${
                  isCompleted ? 'bg-brand' : 'bg-gray-200'
                }`}
              />
            )}
            <div className="flex flex-col items-center gap-1">
              <div
                className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold ${
                  isCompleted
                    ? 'bg-brand text-white'
                    : isCurrent
                      ? 'border-2 border-brand bg-white text-brand'
                      : 'border-2 border-gray-200 bg-white text-gray-400'
                }`}
              >
                {isCompleted ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                ) : (
                  idx + 1
                )}
              </div>
              <span
                className={`hidden text-center text-sm leading-tight sm:block ${
                  isCurrent
                    ? 'font-semibold text-brand'
                    : isCompleted
                      ? 'font-medium text-brand'
                      : 'text-gray-400'
                } ${isUpcoming ? '' : ''}`}
              >
                {step.label}
              </span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

function setParametersLooksFilled(data) {
  const p = data?.parameters || {};
  return (
    String(data?.toolName || '').trim() !== '' ||
    String(p.projectedSales ?? '').trim() !== '' ||
    String(p.projectedGc ?? '').trim() !== ''
  );
}

function stepFromApiPayload(payload) {
  const raw =
    payload?.step ??
    payload?.current_step ??
    payload?.wizard_step ??
    payload?.data?.step ??
    payload?.data?.current_step ??
    payload?.my_tool?.step ??
    payload?.data?.my_tool?.step;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0 || n > 3) return null;
  return n;
}

function resumeStepFromWizardData(data, savedStep) {
  const fromSaved =
    savedStep != null && Number.isFinite(Number(savedStep))
      ? Math.min(3, Math.max(0, Number(savedStep)))
      : null;
  if (fromSaved != null && fromSaved >= 1) return fromSaved;
  if (canProceed(2, data)) return 3;
  if (setParametersLooksFilled(data)) return 2;
  return 1;
}

function DraftToolPrompt({ toolName, onContinue, onDiscard, isDiscarding, isContinuing }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      role="dialog"
      aria-modal="true"
      aria-label="Incomplete draft tool"
    >
      <div className="w-full max-w-md rounded-2xl bg-white p-4 shadow-2xl sm:p-6">
        <h2 className="text-lg font-semibold text-gray-900">Incomplete tool found</h2>
        <p className="mt-2 text-sm text-gray-600">
          A projected tool
          {toolName ? (
            <>
              {' '}
              <span className="font-semibold text-gray-900">{toolName}</span>
            </>
          ) : null}{' '}
          was already started and is not completed yet. Continue editing it, or discard it to start
          over.
        </p>
        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onDiscard}
            disabled={isDiscarding || isContinuing}
            className="rounded-lg border border-[#e8e4df] bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-[#faf8f5] disabled:opacity-50"
          >
            {isDiscarding ? 'Discarding…' : 'Discard'}
          </button>
          <button
            type="button"
            onClick={onContinue}
            disabled={isDiscarding || isContinuing}
            className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            {isContinuing ? 'Loading…' : 'Continue editing'}
          </button>
        </div>
      </div>
    </div>
  );
}

function toolSaveError(err) {
  return (
    err?.data?.message ||
    err?.data?.error ||
    err?.message ||
    'Failed to save tool. Please try again.'
  );
}

function BillToFlowWizard() {
  const { user } = useAuth();
  const [updateMyTool, { isLoading: isUpdatingTool }] = useUpdateMyToolMutation();
  const [deleteMyTool, { isLoading: isDiscardingDraft }] = useDeleteMyToolMutation();
  const [fetchDraft] = useLazyGetMyToolDraftQuery();
  const [fetchToolById] = useLazyGetMyToolByIdQuery();
  const { data: productTypesRes } = useGetProductTypesQuery({
    status: true,
    per_page: 500,
  });
  const [currentStep, setCurrentStep] = useState(0);
  const [wizardData, setWizardData] = useState(INITIAL_DATA);
  const [draftPayload, setDraftPayload] = useState(null);
  const [isContinuingDraft, setIsContinuingDraft] = useState(false);
  const isSavingTool = isUpdatingTool;
  const showDraftPrompt = currentStep === 1 && Boolean(draftPayload);
  const toolIdForShow = String(wizardData.id || '').trim();
  const { data: toolShowRes } = useGetMyToolByIdQuery(toolIdForShow, {
    skip: currentStep < 2 || !toolIdForShow,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchDraft(undefined, false).unwrap();
        if (cancelled) return;
        if (isIncompleteMyToolDraft(res)) setDraftPayload(res);
      } catch {
        // No draft, or the request failed — start a new tool.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchDraft]);

  useEffect(() => {
    const id = String(wizardData.id || '').trim();
    if (!id) return;
    if (currentStep >= 4) {
      clearDraftWizardStep(id);
      return;
    }
    saveDraftWizardStep(id, currentStep);
  }, [wizardData.id, currentStep]);

  useEffect(() => {
    if (!toolShowRes) return;
    setWizardData((prev) => {
      const patch = wizardStateFromMyToolResponse(toolShowRes, prev);
      const sameIds =
        JSON.stringify(prev.myToolProjectIds || {}) === JSON.stringify(patch.myToolProjectIds || {});
      const sameRows =
        JSON.stringify(prev.closingStockDraft?.rows || []) ===
        JSON.stringify(patch.closingStockDraft?.rows || []);
      const sameSku =
        JSON.stringify(prev.planningBySku || {}) === JSON.stringify(patch.planningBySku || {});
      const sameSub =
        JSON.stringify(prev.planningBySubcategory || {}) ===
        JSON.stringify(patch.planningBySubcategory || {});
      if (sameIds && sameRows && sameSku && sameSub) return prev;
      return { ...prev, ...patch };
    });
  }, [toolShowRes]);

  useEffect(() => {
    const currentId = String(wizardData.productTypeId || wizardData.toolType || '').trim();
    const currentName = String(wizardData.productTypeName || wizardData.toolType || '').trim();
    if (!currentId && !currentName) return;
    const types = listFromApiResponse(productTypesRes)
      .filter((row) => row?.id != null && row.status !== false)
      .map((row) => ({
        id: String(row.id),
        name: String(row.name || '').trim(),
      }));
    if (!types.length) return;
    const match =
      types.find((type) => type.id === currentId) ||
      types.find(
        (type) => type.name.toLowerCase() === currentName.toLowerCase() || type.name.toLowerCase() === currentId.toLowerCase()
      );
    if (!match) return;
    if (wizardData.productTypeId === match.id && wizardData.productTypeName === match.name) return;
    setWizardData((prev) => ({
      ...prev,
      toolType: match.id,
      productTypeId: match.id,
      productTypeName: match.name,
    }));
  }, [
    productTypesRes,
    wizardData.productTypeId,
    wizardData.productTypeName,
    wizardData.toolType,
  ]);

  const productTypeIdRaw = String(wizardData.productTypeId || wizardData.toolType || '').trim();
  const productTypeIdNum = Number(productTypeIdRaw);
  const wizardProductTypeId =
    productTypeIdRaw && Number.isFinite(productTypeIdNum) ? productTypeIdNum : null;
  const hasWizardWeeks = String(wizardData.parameters?.salesHistoryWeeks ?? '').trim() !== '';
  const dailySalesArgs = useMemo(() => {
    if (wizardProductTypeId == null || !hasWizardWeeks || !wizardData.toolDate) return null;
    return {
      bill_date: wizardData.toolDate,
      page: 1,
      per_page: 2000,
      product_type_id: wizardProductTypeId,
    };
  }, [hasWizardWeeks, wizardData.toolDate, wizardProductTypeId]);

  const { data: dailySalesRes } = useGetDailySalesQuery(dailySalesArgs, {
    skip: !dailySalesArgs,
  });

  useEffect(() => {
    if (!dailySalesArgs || dailySalesRes == null) return;
    const rows = listDailySalesFromApi(dailySalesRes).map(mapApiDailySaleRow).filter(Boolean);
    setWizardData((prev) => {
      const prevRows = prev.dailySalesRows;
      const same =
        Array.isArray(prevRows) &&
        prevRows.length === rows.length &&
        prevRows.every((row, index) => row.id === rows[index]?.id);
      if (same) return prev;
      return { ...prev, dailySalesRows: rows };
    });
  }, [dailySalesArgs, dailySalesRes]);

  const updateWizardData = useCallback((partial) => {
    setWizardData((prev) => ({
      ...prev,
      ...partial,
      ...(partial.parameters
        ? { parameters: { ...prev.parameters, ...partial.parameters } }
        : {}),
    }));
  }, []);

  const applyToolResponse = useCallback(
    (res, fallbackStatus) => {
      const mapped = myToolRowFromCreateResponse(res);
      const nextId = myToolIdFromResponse(res);
      const createdBy =
        mapped?.createdBy ||
        user?.name ||
        user?.employee_name ||
        user?.account_name ||
        '';
      setWizardData((prev) => ({
        ...prev,
        id: nextId || prev.id,
        toolCode: mapped?.toolCode || prev.toolCode,
        toolName: mapped?.toolName || prev.toolName,
        toolStatus: mapped?.status || fallbackStatus || prev.toolStatus,
        createdBy: createdBy || prev.createdBy,
        myToolProjectIds: mergeMyToolProjectIds(
          prev.myToolProjectIds,
          myToolProjectIdsFromResponse(res)
        ),
      }));
      return nextId;
    },
    [user]
  );

  const persistTool = useCallback(
    async (snapshot, status) => {
      const id = String(snapshot?.id || '').trim();
      if (!id) {
        throw new Error('Upload closing stock CSV first so the draft tool can be updated.');
      }
      const payload = buildCreateMyToolPayload(snapshot, { status });
      if (!payload.my_tool.my_tool_projects_attributes.length) {
        throw new Error('Select at least one SKU before saving the tool.');
      }
      return updateMyTool({ id, body: payload }).unwrap();
    },
    [updateMyTool]
  );

  const handleCsvImported = async ({ closingStockDraft, importResult }) => {
    const importedId = myToolIdFromResponse(importResult);
    const mapped = myToolRowFromCreateResponse(importResult);
    const toolId = importedId || String(wizardData.id || '').trim();
    if (!toolId) {
      throw new Error('CSV imported, but no tool id was returned to update.');
    }
    const patch = wizardStateFromMyToolResponse(importResult, {
      ...wizardData,
      closingStockDraft,
    });
    setWizardData((prev) => ({
      ...prev,
      ...patch,
      closingStockDraft: patch.closingStockDraft || closingStockDraft,
      id: toolId,
      toolCode: mapped?.toolCode || prev.toolCode,
      toolName: mapped?.toolName || prev.toolName,
      toolStatus: mapped?.status || 'draft',
    }));
  };

  const goNext = async () => {
    if (!(currentStep < STEPS.length - 1 && canProceed(currentStep, wizardData))) return;
    if (currentStep >= 2) {
      try {
        const res = await persistTool(wizardData, 'draft');
        applyToolResponse(res, 'draft');
      } catch (err) {
        toast.error(toolSaveError(err));
        return;
      }
    }
    setCurrentStep((s) => {
      const next = s + 1;
      saveDraftWizardStep(wizardData.id, next);
      return next;
    });
  };

  const goBack = () => {
    if (currentStep > 0) {
      const next = currentStep - 1;
      saveDraftWizardStep(wizardData.id, next);
      setCurrentStep(next);
    }
  };

  const handleCompleteTool = async () => {
    try {
      const res = await persistTool(wizardData, 'completed');
      applyToolResponse(res, 'completed');
      toast.success('Tool created successfully.');
      clearDraftWizardStep(wizardData.id);
      setCurrentStep(4);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Complete my tool failed:', err);
      toast.error(toolSaveError(err));
    }
  };

  const handleReset = () => {
    clearDraftWizardStep(wizardData.id);
    setWizardData(INITIAL_DATA);
    setCurrentStep(0);
  };

  const handleContinueDraft = async () => {
    const id = myToolIdFromResponse(draftPayload);
    let payload = draftPayload;
    setIsContinuingDraft(true);
    try {
      if (id) {
        try {
          const full = await fetchToolById(id, false).unwrap();
          const fullProducts = myToolRowFromCreateResponse(full)?.products || [];
          const draftProducts = myToolRowFromCreateResponse(payload)?.products || [];
          if (full && fullProducts.length >= draftProducts.length) payload = full;
        } catch {
          // Fall back to the draft payload if show-by-id is unavailable.
        }
      }
      const next = hydrateWizardFromMyTool(payload, INITIAL_DATA);
      if (!next) {
        toast.error('Could not load the saved draft.');
        setDraftPayload(null);
        return;
      }
      const restored = { ...INITIAL_DATA, ...next };
      const savedStep = loadDraftWizardStep(id) ?? stepFromApiPayload(payload);
      setWizardData(restored);
      setCurrentStep(resumeStepFromWizardData(restored, savedStep));
      setDraftPayload(null);
    } finally {
      setIsContinuingDraft(false);
    }
  };

  const handleDiscardDraft = async () => {
    const id = myToolIdFromResponse(draftPayload);
    if (!id) {
      setDraftPayload(null);
      return;
    }
    try {
      await deleteMyTool(id).unwrap();
      toast.success('Draft discarded.');
      clearDraftWizardStep(id);
      setDraftPayload(null);
      setWizardData((prev) => ({
        ...INITIAL_DATA,
        toolType: prev.toolType,
        productTypeId: prev.productTypeId,
        productTypeName: prev.productTypeName,
      }));
      setCurrentStep(1);
    } catch (err) {
      toast.error(err?.data?.message || err?.data?.error || 'Failed to discard draft.');
    }
  };

  const isLastFormStep = currentStep === 3;
  const isSuccessStep = currentStep === 4;

  return (
    <DashboardLayout title="Create Projected Tool">
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-gray-500">
            <Link to="/dashboard/admin" className="text-brand hover:underline">
              Home
            </Link>
            {' / '}
            <Link to="/dashboard/admin/build-to-flow" className="text-brand hover:underline">
              My Tools
            </Link>
            {' / '}
            <span className="font-medium text-gray-700">Create Projected Tool</span>
          </p>
          <Link
            to="/dashboard/admin/build-to-flow"
            className="text-sm font-medium text-brand hover:underline"
          >
            ← Back to My Tools
          </Link>
        </div>

        {/* Stepper */}
        <div className="rounded-2xl bg-white p-4 shadow-sm sm:px-8 sm:py-5">
          <Stepper currentStep={currentStep} />
        </div>

        {/* Step Content */}
        <div className="min-w-0 rounded-2xl bg-white p-4 shadow-sm sm:p-6">
          {currentStep === 0 && (
            <StepSelectToolType wizardData={wizardData} onChange={updateWizardData} />
          )}
          {currentStep === 1 && (
            <StepConfigureTool
              wizardData={wizardData}
              onChange={updateWizardData}
              onCsvImported={handleCsvImported}
            />
          )}
          {currentStep === 2 && (
            <StepSetParameters wizardData={wizardData} onChange={updateWizardData} />
          )}
          {currentStep === 3 && <StepReviewConfirm wizardData={wizardData} />}
          {currentStep === 4 && (
            <StepToolCreated wizardData={wizardData} onReset={handleReset} />
          )}
        </div>

        {/* Navigation Buttons */}
        {!isSuccessStep && !showDraftPrompt && (
          <div className="flex items-center justify-between pb-6">
            <div>
              {currentStep > 0 && (
                <button
                  type="button"
                  onClick={goBack}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50"
                >
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="19" y1="12" x2="5" y2="12" />
                    <polyline points="12 19 5 12 12 5" />
                  </svg>
                  Back
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={isLastFormStep ? handleCompleteTool : goNext}
              disabled={!canProceed(currentStep, wizardData) || isSavingTool}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-5 py-2.5 text-sm font-semibold text-white shadow-sm disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isLastFormStep
                ? isSavingTool
                  ? 'Saving…'
                  : 'Create Tool'
                : isSavingTool
                  ? 'Saving…'
                  : 'Next'}
              {!isLastFormStep && !isSavingTool && (
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              )}
            </button>
          </div>
        )}
      </div>
      {showDraftPrompt ? (
        <DraftToolPrompt
          toolName={
            myToolRowFromCreateResponse(draftPayload)?.toolName ||
            myToolRowFromCreateResponse(draftPayload)?.toolCode ||
            ''
          }
          onContinue={handleContinueDraft}
          onDiscard={handleDiscardDraft}
          isDiscarding={isDiscardingDraft}
          isContinuing={isContinuingDraft}
        />
      ) : null}
    </DashboardLayout>
  );
}

export default BillToFlowWizard;
